// Private file storage: Cloudflare R2 (migration 026/027) when an Admin has switched R2 uploads on, otherwise the existing
// Supabase Storage bucket. Reads always work for both (dual read): a stored reference starting with "r2:" opens through a
// 5-minute signed R2 link from the r2-files Edge Function; anything else is a Supabase Storage path, exactly as before.
// R2 keys never reach the browser. The database chooses the R2 folder and file name, and only accepts a file after the Edge
// Function has read it back and matched its size and SHA-256.
import { supabase } from "../supabase";

const CONFIG_TTL_MS = 60000;
let cached = null;

export const isR2 = (ref) => typeof ref === "string" && ref.startsWith("r2:");

// Uploads go to R2 only when switched on. Any error (e.g. a database without 026) means "off": the app behaves as before.
export async function r2UploadsEnabled() {
    if (cached && Date.now() - cached.at < CONFIG_TTL_MS) return cached.on;
    const { data, error } = await supabase.rpc("storage_r2_config");
    cached = { on: !error && data?.uploads_enabled === true, at: Date.now() };
    return cached.on;
}
export const forgetR2Config = () => { cached = null; };

async function r2(action, body) {
    const { data, error } = await supabase.functions.invoke("r2-files", { body: { action, ...body } });
    if (error) {
        let message = error.message || "File service unavailable";
        try { const j = await error.context?.json(); if (j?.message) message = j.message; } catch { /* keep the generic message */ }
        throw new Error(message);
    }
    return data;
}

export async function sha256Hex(bytes) {
    const d = await crypto.subtle.digest("SHA-256", bytes);
    return [...new Uint8Array(d)].map(b => b.toString(16).padStart(2, "0")).join("");
}

async function toBytes(data) {
    if (data instanceof Uint8Array) return data;
    if (typeof data.arrayBuffer === "function") return new Uint8Array(await data.arrayBuffer());
    return new Uint8Array(await new Promise((resolve, reject) => {        // older Blob implementations
        const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = () => reject(r.error); r.readAsArrayBuffer(data);
    }));
}
// Store one file. `target` names the record and document type for R2 (e.g. "sales.invoice_pdf"); `legacy` is the
// Supabase Storage bucket/path used when R2 uploads are off. Returns { ref, sha256, size, mime }: `ref` is what the
// record keeps (an "r2:" reference or the Supabase path) and `sha256` is the hash of exactly the bytes stored.
// `blob` may be a File/Blob or the bytes themselves (generated PDFs).
export async function storeFile({ target, ownerId = null, blob, fileName, mime, legacy }) {
    const bytes = await toBytes(blob);
    const sha256 = await sha256Hex(bytes);
    if (await r2UploadsEnabled()) {
        const s = await r2("sign-upload", { target, owner_id: ownerId, file_name: fileName || "", mime, size: bytes.byteLength, sha256 });
        const put = await fetch(s.url, { method: "PUT", headers: s.headers, body: bytes });
        if (!put.ok) throw new Error(put.status === 412 ? "Upload refused: the file already exists" : `Upload failed (${put.status})`);
        await r2("confirm", { id: s.id });
        return { ref: s.ref, sha256, size: bytes.byteLength, mime };
    }
    const { error } = await supabase.storage.from(legacy.bucket)
        .upload(legacy.path, new Blob([bytes], { type: mime }), { contentType: legacy.contentType ?? mime, upsert: !!legacy.upsert });
    if (error) throw new Error(`Upload failed: ${error.message}`);
    return { ref: legacy.path, sha256, size: bytes.byteLength, mime };
}

// A short-lived link to show or open a stored file (5 minutes).
export async function fileUrl(bucket, ref) {
    if (isR2(ref)) return (await r2("sign-download", { ref })).url;
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(ref, 300);
    if (error) throw new Error(error.message);
    return data.signedUrl;
}

// The stored file's bytes (for integrity checks, PDFs and images).
export async function fileBlob(bucket, ref) {
    if (isR2(ref)) {
        const res = await fetch(await fileUrl(bucket, ref));
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        return await res.blob();
    }
    const { data, error } = await supabase.storage.from(bucket).download(ref);
    if (error) throw new Error(error.message || "Download failed");
    return data;
}

// Company files with no owning record (the Sales stamp): registered in the R2 registry and found there (newest first).
export async function registerCompanyFile(ref, kind) {
    const { error } = await supabase.rpc("storage_r2_register_company_file", { p_ref: ref, p_kind: kind });
    if (error) throw new Error(error.message);
}
// The newest R2 company file of a kind with its time, or null (none, or a database without 026).
export async function latestCompanyFile(kind) {
    const { data, error } = await supabase.rpc("storage_r2_company_file", { p_kind: kind });
    return !error && data?.ref ? data : null;
}
